/**
 * Shared feedback colors, labels, and style helpers for question components.
 * Single source of truth for correct/incorrect/missed/neutral styling.
 */

export const FEEDBACK_COLORS = {
  correct: { border: '#28a745', bg: '#d4edda', badge: '#28a745' },
  incorrect: { border: '#c0392b', bg: '#fdecea', badge: '#c0392b' },
  missed: { border: '#d4a017', bg: '#fff8e1', badge: '#d4a017' },
  neutral: { border: '#dee2e6', bg: '#f8f9fa' },
}

export const FEEDBACK_LABELS = {
  correct: 'Correct',
  incorrect: 'Incorrect',
  missed: 'Missed',
}

/**
 * Determine feedback status for a simple option (selected vs correct).
 */
export function getOptionFeedbackStatus(isSelected, isCorrect) {
  if (isSelected && isCorrect) return 'correct'
  if (isSelected && !isCorrect) return 'incorrect'
  if (!isSelected && isCorrect) return 'missed'
  return 'neutral'
}

/** Inline style overrides for option feedback states */
export const OPTION_FEEDBACK_STYLE = {
  correct: { borderColor: FEEDBACK_COLORS.correct.border, backgroundColor: FEEDBACK_COLORS.correct.bg },
  incorrect: { borderColor: FEEDBACK_COLORS.incorrect.border, backgroundColor: FEEDBACK_COLORS.incorrect.bg },
  missed: { borderColor: FEEDBACK_COLORS.missed.border, backgroundColor: FEEDBACK_COLORS.missed.bg },
  neutral: { opacity: 0.5 },
}

/** Pre-computed zone feedback row styles (Pattern 2) */
const ZONE_ROW_BASE = {
  display: 'flex', alignItems: 'center', gap: '0.3rem',
  padding: '0.2rem 0.5rem', borderRadius: '5px',
  fontWeight: 400, fontSize: '0.95em', whiteSpace: 'nowrap',
}
export const ZONE_ROW = {
  correct: { ...ZONE_ROW_BASE, backgroundColor: FEEDBACK_COLORS.correct.bg, color: '#155724' },
  missed: { ...ZONE_ROW_BASE, backgroundColor: FEEDBACK_COLORS.missed.bg, color: '#856404' },
  incorrect: { ...ZONE_ROW_BASE, backgroundColor: '#f8d7da', color: '#721c24' },
}

/** Pre-computed zone feedback badge styles (Pattern 2) */
const ZONE_BADGE_BASE = {
  fontSize: '0.65rem', fontWeight: 700,
  padding: '0.05rem 0.3rem', borderRadius: '3px',
  whiteSpace: 'nowrap', marginLeft: 'auto', color: '#fff',
}
export const ZONE_BADGE = {
  correct: { ...ZONE_BADGE_BASE, backgroundColor: FEEDBACK_COLORS.correct.badge },
  missed: { ...ZONE_BADGE_BASE, backgroundColor: FEEDBACK_COLORS.missed.badge },
  incorrect: { ...ZONE_BADGE_BASE, backgroundColor: FEEDBACK_COLORS.incorrect.badge },
}

/** Pre-computed option feedback badge styles (Pattern 1) */
const FEEDBACK_BADGE_BASE = {
  fontSize: '0.7rem', fontWeight: 700,
  padding: '0.1rem 0.4rem', borderRadius: '3px',
  whiteSpace: 'nowrap', marginLeft: 'auto', color: '#fff',
}
export const FEEDBACK_BADGE = {
  correct: { ...FEEDBACK_BADGE_BASE, backgroundColor: FEEDBACK_COLORS.correct.badge },
  incorrect: { ...FEEDBACK_BADGE_BASE, backgroundColor: FEEDBACK_COLORS.incorrect.badge },
  missed: { ...FEEDBACK_BADGE_BASE, backgroundColor: FEEDBACK_COLORS.missed.badge },
}

/** Feedback key swatch base style */
export const FEEDBACK_KEY_SWATCH = {
  display: 'inline-block', width: '14px', height: '14px',
  borderRadius: '3px', border: '2px solid', marginRight: '0.3rem', verticalAlign: 'middle',
}

/** Feedback key container style */
export const FEEDBACK_KEY_STYLE = {
  display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center',
  fontSize: '0.85rem', marginBottom: '0.5rem', color: '#555',
}

/** Feedback key item style */
export const FEEDBACK_KEY_ITEM_STYLE = {
  display: 'inline-flex', alignItems: 'center',
}
